// Copyright (c) Microsoft. All rights reserved.

import React from 'react';
import { IChatMessage } from '../../../../../libs/models/ChatMessage';
import { formatDateString, isDateTodayOrFuture } from '../../../../utils/DateUtils';
import * as utils from './../../../../utils/DocumentUtils';

interface ChatItemDocumentContentProps {
    isMe: boolean;
    message: IChatMessage;
}

interface DocumentData {
    name: string;
    size: string;
    isUploaded: boolean;
    isAssistantUpload?: boolean;
    documentRetentionDate?: string;
}

interface DocumentMessageContent {
    documents: DocumentData[];
}

export const ChatItemDocumentContent: React.FC<ChatItemDocumentContentProps> = ({ message }) => {
    let documents: DocumentData[] = [];
    try {
        ({ documents } = JSON.parse(message.content) as DocumentMessageContent);
    } catch (e) {
        console.error('Error parsing chat history file item: ' + message.content);
    }

    return (
        <>
            {documents.map(({ name, size, isUploaded, isAssistantUpload, documentRetentionDate }, index) => (
                <ul
                    key={`${message.id ?? 'unknown-message-id'}-document-${index}`}
                    role="list"
                    className="grid grid-cols-1 gap-6">
                    <li className="group col-span-1 transition-colors duration-300 sm:grid-cols-4">
                        <div
                            className={`flex items-center rounded-lg shadow justify-between space-x-3 p-3 bg-slate-100 dark:text-white dark:bg-slate-900 rounded-lg shadow`}>
                            <div className="flex flex-col items-center gap-y-1 rounded-lg text-xs">
                                {utils.getFileIconByFileExtension(name)}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                    <h3 className="text-sm font-sm transition-colors duration-300">{name}</h3>
                                </div>

                                <div className="flex justify-between items-center mt-1">
                                    <span className="text-xs text-slate-500 transition-colors duration-300">
                                        {size}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <span className="text-slate-600 dark:text-slate-200 text-xs font-semibold flex mt-2">
                            <span className="p-1 rounded-full bg-slate-200 dark:bg-slate-200 inline-block mr-1">
                                {isUploaded ? (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-green-700"
                                        viewBox="0 0 24 24"
                                        strokeWidth="2"
                                        stroke={'currentColor'}
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M5 12l5 5l10 -10"></path>
                                    </svg>
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-red-700"
                                        viewBox="0 0 24 24"
                                        strokeWidth="2"
                                        stroke="currentColor"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M18 6l-12 12"></path>
                                        <path d="M6 6l12 12"></path>
                                    </svg>
                                )}
                            </span>
                            <span className="inline-block mt-1 font-normal">
                                {isUploaded ? (
                                    <span>Success: document uploaded to chat</span>
                                ) : (
                                    <span>Failed: document not uploaded to chat</span>
                                )}
                            </span>
                        </span>
                        {isAssistantUpload && (
                            <span className="text-slate-600 dark:text-slate-200 text-xs font-semibold flex mt-2">
                                <span className="p-1 rounded-full bg-slate-200 dark:bg-slate-700 inline-block mr-1">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-red-700"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" strokeLinejoin="round"></g>
                                        <g id="SVGRepo_iconCarrier">
                                            {' '}
                                            <g id="Warning">
                                                {' '}
                                                <g>
                                                    {' '}
                                                    <g>
                                                        {' '}
                                                        <path d="M12.5,8.752a.5.5,0,0,0-1,0h0v6a.5.5,0,0,0,1,0Z"></path>{' '}
                                                        <circle cx="11.999" cy="16.736" r="0.5"></circle>{' '}
                                                    </g>{' '}
                                                    <path d="M18.642,20.934H5.385A2.5,2.5,0,0,1,3.163,17.29L9.792,4.421a2.5,2.5,0,0,1,4.444,0L20.865,17.29a2.5,2.5,0,0,1-2.223,3.644ZM12.014,4.065a1.478,1.478,0,0,0-1.334.814L4.052,17.748a1.5,1.5,0,0,0,1.333,2.186H18.642a1.5,1.5,0,0,0,1.334-2.186L13.348,4.879A1.478,1.478,0,0,0,12.014,4.065Z"></path>{' '}
                                                </g>{' '}
                                            </g>{' '}
                                        </g>
                                    </svg>
                                </span>
                                <span className="inline-block mt-1 font-normal text-red-700 dark:text-slate-200">
                                    {documentRetentionDate && isDateTodayOrFuture(documentRetentionDate)
                                        ? `Expires on ${documentRetentionDate && formatDateString(documentRetentionDate)}`
                                        : 'Expired'}
                                </span>
                            </span>
                        )}
                    </li>
                </ul>
            ))}
        </>
    );
};
